/**
 * Tracker konfigurasyonu. Site ayarlari public settings endpoint'inden
 * `settings[0].webAnalytics` olarak gelir (bkz. tablet/apis/settingEndpoint.js).
 * SDK'nin `analytics` komutu ayarin uzerine cikamaz, sadece kisitlar.
 */
import { AUTOCAPTURE_DEFAULTS } from "./events";

const DEFAULT_SEARCH_PARAMS = ["q", "s", "search", "query"];
// Yol tabanli arama kaliplari; bos ise collectors/searchPath.js varsayilanlari kullanilir

/**
 * Sayfa kayit defteri varsayilanlari. Meta toplama ucretsizdir ve varsayilan
 * aciktir; AI analizi ayri bir opt-in'dir ve cuzdandan ucretlendirilir.
 */
const DEFAULT_PAGES = {
    collectMeta: true,
    ai: { enabled: false, deriveEvents: true },
    excludedPaths: [],
    keepQueryParams: [],
    contentSelector: "",
    removeSelectors: [],
    respectNoindex: true,
    maxContentBytes: 40 * 1024,
};

function toBool(value, fallback) {
    if (value === true || value === false) return value;
    return fallback;
}

class TrackerConfig {
    constructor() {
        this.siteId = null;
        this.endpoint = null;
        this.autocapture = Object.assign({}, AUTOCAPTURE_DEFAULTS);
        this.siteSearchParams = DEFAULT_SEARCH_PARAMS.slice();
        this.siteSearchPaths = [];
        this.excludedPaths = [];
        this.maskSelectors = [];
        this.respectDoNotTrack = false;
        this.sessionTimeoutMinutes = 30;
        this.pages = JSON.parse(JSON.stringify(DEFAULT_PAGES));
    }

    /** Site ayarindan gelen webAnalytics nesnesini uygular. */
    apply(raw) {
        const settings = raw && typeof raw === "object" ? raw : {};
        const capture = settings.autocapture && typeof settings.autocapture === "object" ? settings.autocapture : {};

        const merged = {};
        const keys = Object.keys(AUTOCAPTURE_DEFAULTS);
        for (let i = 0; i < keys.length; i++) {
            merged[keys[i]] = toBool(capture[keys[i]], AUTOCAPTURE_DEFAULTS[keys[i]]);
        }
        this.autocapture = merged;

        if (Array.isArray(settings.siteSearchParams) && settings.siteSearchParams.length > 0) {
            this.siteSearchParams = settings.siteSearchParams.filter(function (x) {
                return typeof x === "string" && x.length > 0;
            });
        }
        if (Array.isArray(settings.siteSearchPaths)) {
            this.siteSearchPaths = settings.siteSearchPaths.filter(function (x) {
                return typeof x === "string" && x.length > 1 && x.charAt(0) === "/";
            });
        }
        if (Array.isArray(settings.excludedPaths)) {
            this.excludedPaths = settings.excludedPaths.filter(function (x) {
                return typeof x === "string" && x.length > 0;
            });
        }
        if (Array.isArray(settings.maskSelectors)) {
            this.maskSelectors = settings.maskSelectors.filter(function (x) {
                return typeof x === "string" && x.length > 0;
            });
        }
        this.respectDoNotTrack = toBool(settings.respectDoNotTrack, false);
        const timeout = parseInt(settings.sessionTimeoutMinutes, 10);
        if (timeout > 0) this.sessionTimeoutMinutes = timeout;

        this.applyPages(settings.pages);
    }

    /** Sayfa kayit defteri ayarlari (public settings payload'indaki `pages` blogu). */
    applyPages(raw) {
        const source = raw && typeof raw === "object" ? raw : {};
        const pages = JSON.parse(JSON.stringify(DEFAULT_PAGES));

        pages.collectMeta = toBool(source.collectMeta, DEFAULT_PAGES.collectMeta);
        pages.respectNoindex = toBool(source.respectNoindex, DEFAULT_PAGES.respectNoindex);

        const ai = source.ai && typeof source.ai === "object" ? source.ai : {};
        pages.ai.enabled = toBool(ai.enabled, false);
        pages.ai.deriveEvents = toBool(ai.deriveEvents, true);

        const arrayKeys = ["excludedPaths", "keepQueryParams", "removeSelectors"];
        for (let i = 0; i < arrayKeys.length; i++) {
            const key = arrayKeys[i];
            if (Array.isArray(source[key])) {
                pages[key] = source[key].filter(function (x) {
                    return typeof x === "string" && x.length > 0;
                });
            }
        }

        if (typeof source.contentSelector === "string") pages.contentSelector = source.contentSelector;
        const maxBytes = parseInt(source.maxContentBytes, 10);
        if (maxBytes > 0) pages.maxContentBytes = Math.min(maxBytes, DEFAULT_PAGES.maxContentBytes);

        this.pages = pages;
    }

    /**
     * SDK override: yalnizca kapatma yonunde etki eder.
     * Ayarda kapali olan bir collector sayfa kodundan acilamaz.
     */
    override(raw) {
        if (!raw || typeof raw !== "object") return;
        const capture = raw.autocapture && typeof raw.autocapture === "object" ? raw.autocapture : {};
        const keys = Object.keys(capture);
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            if (this.autocapture[key] === undefined) continue;
            if (capture[key] === false) this.autocapture[key] = false;
        }
    }

    isEnabled(key) {
        return this.autocapture[key] === true;
    }

    /** Do Not Track sinyali ayardan saygi gosterilecek sekilde isaretlendiyse izleme yapilmaz. */
    isDoNotTrack() {
        if (!this.respectDoNotTrack) return false;
        try {
            const dnt = navigator.doNotTrack || window.doNotTrack || navigator.msDoNotTrack;
            return dnt === "1" || dnt === "yes";
        } catch (e) {
            return false;
        }
    }

    /**
     * Haric tutulan yol kontrolu. Basit glob (* ve ?) destegi;
     * ornek: /admin/*, /checkout/step-?
     */
    isExcludedPath(path) {
        if (!this.excludedPaths.length) return false;
        const target = path || window.location.pathname;
        for (let i = 0; i < this.excludedPaths.length; i++) {
            const pattern = this.excludedPaths[i];
            const escaped = pattern.replace(/[.+^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*").replace(/\?/g, ".");
            try {
                if (new RegExp("^" + escaped + "$").test(target)) return true;
            } catch (e) {
                if (target.indexOf(pattern) === 0) return true;
            }
        }
        return false;
    }

    /** Metin toplanmamasi gereken element mi (maskSelectors). */
    isMasked(element) {
        if (!this.maskSelectors.length || !element || !element.closest) return false;
        for (let i = 0; i < this.maskSelectors.length; i++) {
            try {
                if (element.closest(this.maskSelectors[i])) return true;
            } catch (e) {
                /* gecersiz selector yoksayilir */
            }
        }
        return false;
    }
}

export const trackerConfig = new TrackerConfig();
export default trackerConfig;



//////////////////
// WEBPACK FOOTER
// ./static/src/tracker/config.js
// module id = null
// module chunks = 